<!--
  Non-blocking "Ponto encerrado" toast. Shown when `LiveAnalysisLoop` closes
  a rally. Tapping P1 or P2 awards the point via `awardPoint` from the
  parent's MatchState; ignoring it causes auto-dismiss after 10s. Parents
  wire it by conditionally rendering when `rallyPending !== null`.
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import type { Player } from '@tbc/core';

  interface Props {
    /** P1/P2 display metadata (for colors + names). */
    players?: readonly Player[];
    /** Winner picker callback. */
    onPick: (winner: 'p1' | 'p2') => void;
    /** Auto-dismiss timeout; defaults to 10s. */
    timeoutMs?: number;
    /** Fires when the toast auto-dismisses (no user action). */
    onDismiss?: () => void;
  }

  const { players = [], onPick, timeoutMs = 10_000, onDismiss }: Props = $props();

  const p1Color = $derived(players.find((p) => p.id === 'p1')?.color ?? '#22c55e');
  const p2Color = $derived(players.find((p) => p.id === 'p2')?.color ?? '#06b6d4');
  const p1Name = $derived(players.find((p) => p.id === 'p1')?.name ?? 'P1');
  const p2Name = $derived(players.find((p) => p.id === 'p2')?.name ?? 'P2');

  // svelte-ignore state_referenced_locally
  let remaining = $state(timeoutMs);
  const startedAt = Date.now();
  const timer = setInterval(() => {
    remaining = Math.max(0, timeoutMs - (Date.now() - startedAt));
    if (remaining <= 0) {
      clearInterval(timer);
      onDismiss?.();
    }
  }, 200);

  onDestroy(() => clearInterval(timer));

  function pick(w: 'p1' | 'p2') {
    clearInterval(timer);
    onPick(w);
  }
</script>

<div class="toast" role="dialog" aria-live="polite" aria-label="Ponto encerrado — escolha o vencedor">
  <div class="head">
    <div class="title">Ponto encerrado</div>
    <div class="sub">Quem ganhou?</div>
  </div>
  <div class="actions">
    <button type="button" class="pick" style:--c={p1Color} onclick={() => pick('p1')}>
      {p1Name}
    </button>
    <button type="button" class="pick" style:--c={p2Color} onclick={() => pick('p2')}>
      {p2Name}
    </button>
  </div>
  <div class="bar" aria-hidden="true">
    <div class="bar-fill" style:width={`${(remaining / timeoutMs) * 100}%`}></div>
  </div>
</div>

<style>
  .toast {
    position: absolute;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(15, 23, 42, 0.92);
    color: #e2e8f0;
    border: 1px solid rgba(148, 163, 184, 0.3);
    border-radius: 14px;
    font-family: system-ui, sans-serif;
    min-width: 280px;
    max-width: 420px;
    box-shadow: 0 20px 60px -20px rgba(0, 0, 0, 0.8);
    z-index: 50;
  }
  .head { display: flex; flex-direction: column; gap: 2px; }
  .title { font-size: 15px; font-weight: 700; }
  .sub { font-size: 12px; color: #94a3b8; }
  .actions { display: flex; gap: 8px; }
  .pick {
    flex: 1;
    padding: 10px 14px;
    border: none;
    border-radius: 8px;
    background: var(--c);
    color: #0f172a;
    font-weight: 700;
    font-size: 14px;
    font-family: inherit;
    cursor: pointer;
  }
  .pick:active { transform: scale(0.97); }
  .bar { height: 3px; background: rgba(148, 163, 184, 0.2); border-radius: 2px; overflow: hidden; }
  .bar-fill { height: 100%; background: rgba(226, 232, 240, 0.6); transition: width 0.2s linear; }
</style>
